import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { useMutation } from '@apollo/client'
import { Container } from '@mui/material'
import { getContentReferenceTypesContentDictionary } from 'services/content-references'
import {
  CONTENT_REFERENCE_TYPE_ADMIN_CATEGORY,
  CONTENT_REFERENCE_TYPE_CATEGORY,
  CONTENT_REFERENCE_TYPE_RAINBOW_CATEGORY,
  CONTENT_REFERENCE_TYPE_CENTERS,
  CONTENT_REFERENCE_TYPE_MARKETING_CENTERS,
  CONTENT_REFERENCE_TYPE_CONTENT_ORIGIN,
  CONTENT_REFERENCE_TYPE_GENRES,
  CONTENT_REFERENCE_TYPE_INTERVIEW_GUEST,
  CONTENT_REFERENCE_TYPE_INTERVIEW_HOST,
  CONTENT_REFERENCE_TYPE_SIMILAR_VIDEOS,
  CONTENT_REFERENCE_TYPE_THIRD_PARTY_GENRE,
} from 'services/content-references/types'
import SectionAccordion from 'gui/components/accordion/section-accordion'
import ContentReferenceSection from './content-references/ContentReferenceSection'
import { SINGLE_SELECT_REFERENCE_TYPES } from './constants'
import {
  transformGraphQLToComponent,
  areReferencesEqual,
  compareReferences,
} from './content-references/utils'
import { CREATE_CONTENT_REFERENCES, DELETE_CONTENT_REFERENCE } from './content-references/queries'
import type { ContentReference, ContentReferenceMap, SectionManagedSectionProps } from './types'

const COURSE_CONTENT_REFERENCE_TYPES = [
  CONTENT_REFERENCE_TYPE_ADMIN_CATEGORY,
  CONTENT_REFERENCE_TYPE_CATEGORY,
  CONTENT_REFERENCE_TYPE_RAINBOW_CATEGORY,
  CONTENT_REFERENCE_TYPE_CENTERS,
  CONTENT_REFERENCE_TYPE_MARKETING_CENTERS,
  CONTENT_REFERENCE_TYPE_CONTENT_ORIGIN,
  CONTENT_REFERENCE_TYPE_GENRES,
  CONTENT_REFERENCE_TYPE_INTERVIEW_GUEST,
  CONTENT_REFERENCE_TYPE_INTERVIEW_HOST,
  CONTENT_REFERENCE_TYPE_SIMILAR_VIDEOS,
  CONTENT_REFERENCE_TYPE_THIRD_PARTY_GENRE,
]

export interface CourseContentReferencesSectionContentData {
  id?: string
  contentId?: number
  contentType?: string
  contentReferencesFrom?: Array<{
    id: string
    contentReferenceType?: { type: string } | null
    contentTo?: Array<{
      id: string
      contentId: number
      contentType: string
      title: string
      published: boolean
      titleGroupId: string
    } | null> | null
  } | null> | null
}

export interface CourseContentReferencesSectionProps extends SectionManagedSectionProps {
  contentData: CourseContentReferencesSectionContentData | null
}

/**
 * Course Content References Section (section-managed).
 * Owns state and mutations; exposes save handler via saveHandlerRef.
 * Uses SINGLE_SELECT_REFERENCE_TYPES for single-select fields (e.g. admin_category, content_origin).
 *
 * Query dependency: This section expects contentData.contentReferencesFrom to include all types
 * in COURSE_CONTENT_REFERENCE_TYPES. The page's GraphQL query (GET_COURSE_CONTENT in queries.ts)
 * must request contentReferencesFrom(types: [these types]). If the query omits them, existing refs
 * won't load and saved refs will disappear after refetch.
 */
function CourseContentReferencesSection ({
  contentData,
  isViewOnly,
  onDirtyChange,
  saveHandlerRef,
  onResetTriggered,
}: CourseContentReferencesSectionProps) {
  const initialContentReferences: ContentReferenceMap = useMemo(() => {
    const referencesFrom = contentData?.contentReferencesFrom
    if (!referencesFrom) return {}

    const validRefs = referencesFrom.filter((ref): ref is NonNullable<typeof ref> => ref != null)
    const allReferences = transformGraphQLToComponent(validRefs)

    const grouped: ContentReferenceMap = {}
    COURSE_CONTENT_REFERENCE_TYPES.forEach((refType) => {
      grouped[refType] = allReferences.filter((ref) => ref.contentReferenceType === refType)
    })
    return grouped
  }, [contentData])

  const [contentReferences, setContentReferences] = useState<ContentReferenceMap>({})

  useEffect(() => {
    setContentReferences(initialContentReferences)
  }, [initialContentReferences])

  const isDirty = useMemo(
    () =>
      COURSE_CONTENT_REFERENCE_TYPES.some((refType) => {
        const initial = initialContentReferences[refType] || []
        const current = contentReferences[refType] || []
        return !areReferencesEqual(initial, current)
      }),
    [initialContentReferences, contentReferences],
  )

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  const handleContentReferencesChange = useCallback(
    (referenceType: string, newReferences: ContentReference[]) =>
      setContentReferences((prev) => ({
        ...prev,
        [referenceType]: newReferences,
      })),
    [],
  )

  const [createContentReferences] = useMutation(CREATE_CONTENT_REFERENCES)
  const [deleteContentReference] = useMutation(DELETE_CONTENT_REFERENCE)

  const handleSave = useCallback(async () => {
    const allCurrentReferences: ContentReference[] = []
    const allDeletions: ContentReference[] = []

    COURSE_CONTENT_REFERENCE_TYPES.forEach((refType) => {
      const current = contentReferences[refType] || []
      const initial = initialContentReferences[refType] || []
      const { deletions } = compareReferences(initial, current)
      current.forEach((item) => {
        allCurrentReferences.push({ ...item, contentReferenceType: refType })
      })
      allDeletions.push(...deletions)
    })

    if (allCurrentReferences.length === 0 && allDeletions.length === 0) {
      return
    }

    if (allDeletions.length > 0) {
      await Promise.all(
        allDeletions.map((item) =>
          deleteContentReference({
            variables: { contentReference: { id: item.referenceId } },
          }),
        ),
      )
    }

    if (allCurrentReferences.length > 0) {
      const contentReferencesToCreate = allCurrentReferences.map((item) => ({
        fromContentContentId: contentData?.contentId,
        fromContentContentType: contentData?.contentType,
        toContentContentId: item.contentId,
        toContentContentType: item.contentType,
        type: item.contentReferenceType,
        sortKey: null,
      }))
      await createContentReferences({
        variables: {
          contentReferences: contentReferencesToCreate,
          deleteOmittedReferences: false,
        },
      })
    }
  }, [
    contentReferences,
    initialContentReferences,
    contentData,
    createContentReferences,
    deleteContentReference,
  ])

  useEffect(() => {
    const ref = saveHandlerRef
    ref.current = handleSave
    return () => {
      ref.current = null
    }
  }, [saveHandlerRef, handleSave])

  useEffect(() => {
    if (onResetTriggered !== undefined && onResetTriggered > 0) {
      setContentReferences(initialContentReferences)
    }
  }, [onResetTriggered, initialContentReferences])

  return (
    <Container className="content-detail__section content-references" maxWidth={false}>
      <SectionAccordion title="Content References" collapsed={false}>
        {COURSE_CONTENT_REFERENCE_TYPES.map((referenceType) => {
          // Config from service dictionary uses Immutable.js
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const config: any = getContentReferenceTypesContentDictionary().get(referenceType)
          if (!config) return null

          return (
            <ContentReferenceSection
              key={referenceType}
              label={config.get('label')}
              value={contentReferences[referenceType] || []}
              onChange={(newReferences) =>
                handleContentReferencesChange(referenceType, newReferences)
              }
              disabled={isViewOnly}
              contentType={config.get('contentType')?.first?.() || config.get('contentType')}
              bundle={config.get('bundle')}
              filterType={config.get('filterType')}
              multiple={!SINGLE_SELECT_REFERENCE_TYPES.includes(referenceType)}
            />
          )
        })}
      </SectionAccordion>
    </Container>
  )
}

export default CourseContentReferencesSection
